import PropTypes from 'prop-types';
import { Warehouse, GitBranch } from 'lucide-react';
import { isModuleOn, getOrgSettings } from '../data/store';
import { CustomSelect } from './CustomSelect';

// Mode 0 = no warehouse routing in use yet; we don't render a chip for it.
// Modes 1-3 describe how a warehouse routes stock to branches.
const MODE_LABEL = {
  1: 'Mode 1 · Shared warehouse & purchases',
  2: 'Mode 2 · Shared warehouse · branch purchases',
  3: 'Mode 3 · Independent per branch',
};

// Location switcher used by Purchases & Inventory. `scope` is 'branch' | 'central'.
// When 'central' it shows a fixed Central Warehouse chip; when 'branch' a branch picker.
// When `lockedBranchId` is set the picker is replaced by a fixed branch chip
// (branch-admins and org-users are confined to their own branch).
export const StockLocationBar = ({ org, scope, mode, branches, locationId, onChange, accent, lockedBranchId }) => {
  const warehouseEnabled = org ? isModuleOn(org, 'warehouse') : true;
  const branchesEnabled = org ? getOrgSettings(org.id).branchesEnabled : true;

  if (!warehouseEnabled && !branchesEnabled) {
    return null; // Don't render anything if both warehouse and multi-branch are off
  }

  const isCentral = scope === 'central';
  const lockedBranch = lockedBranchId ? branches.find((b) => b.id === lockedBranchId) : null;

  return (
    <div className="flex flex-wrap items-center gap-3">
      {warehouseEnabled && MODE_LABEL[mode] && (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-semibold uppercase tracking-[0.14em]"
          style={{ background: 'var(--theme-primary-soft)', color: accent }}>
          {MODE_LABEL[mode]}
        </span>
      )}

      {isCentral ? (
        <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-white text-xs font-semibold text-[#0d0d0d]"
          style={{ borderColor: 'var(--theme-tertiary)' }}>
          Central Warehouse
        </span>
      ) : (lockedBranch || !branchesEnabled) ? (
        <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-white text-xs font-semibold text-[#0d0d0d]"
          style={{ borderColor: 'var(--theme-tertiary)' }}>
          {lockedBranch ? lockedBranch.name : (branches[0]?.name || 'Main branch')}
        </span>
      ) : (
        <div className="min-w-[160px]">
          <CustomSelect
            value={locationId}
            onChange={onChange}
            options={branches.map((b) => ({ value: b.id, label: `${b.name}${b.isMain ? ' (Main)' : ''}` }))}
            className="w-full border-gray-100 bg-white text-xs font-medium normal-case tracking-normal py-2 px-3 rounded-md min-w-[130px]"
          />
        </div>
      )}
    </div>
  );
};

StockLocationBar.propTypes = {
  org: PropTypes.object,
  scope: PropTypes.oneOf(['branch', 'central']).isRequired,
  mode: PropTypes.number,
  branches: PropTypes.arrayOf(PropTypes.object).isRequired,
  locationId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  accent: PropTypes.string.isRequired,
  lockedBranchId: PropTypes.string,
};
